import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { MaterialsProps, MaterialsFilterType } from "../../types/material";

export interface MaterialsState {
  materials: MaterialsProps[];
  totalMaterials: number;
  filter: MaterialsFilterType;
  isMaterialModalOpen: boolean;
  editMaterial: MaterialsProps | null;
  loading: boolean;
  error: string | null;
}

const initialState: MaterialsState = {
  materials: [],
  totalMaterials: 0,
  filter: {
    search: null,
    sortBy: "id",
    sortOrder: "desc",
  },
  isMaterialModalOpen: false,
  editMaterial: null,
  loading: false,
  error: null,
};

type FetchMaterialsListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  sortBy?: string;
  sortOrder?: string;
};

export const fetchMaterialsListData = createAsyncThunk(
  "materials/fetchList",
  async (req: FetchMaterialsListDataProps) => {
    let response: any;
    await GET(`/v1/materials`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const materialsSlice = createSlice({
  name: "materials",
  initialState,
  reducers: {
    toggleMaterialModal: (state, action: PayloadAction<boolean>) => {
      state.isMaterialModalOpen = action.payload;
    },
    setEditMaterial: (state, action: PayloadAction<MaterialsProps | null>) => {
      state.editMaterial = action.payload;
    },
    setMaterialsFilter: (state, action: PayloadAction<Partial<MaterialsFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMaterialsListData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMaterialsListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.materials = action.payload.data.data.records;
        state.totalMaterials = action.payload.data.data.total_records;
        state.error = null;
      } else {
        state.materials = [];
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchMaterialsListData.rejected, (state, action: any) => {
      state.materials = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "Something went wrong. Please try again.";
    });
  },
});

export const { toggleMaterialModal, setEditMaterial, setMaterialsFilter } = materialsSlice.actions;

export const getMaterials = (state: RootState) => state.materials.materials;
export const getTotalMaterials = (state: RootState) => state.materials.totalMaterials;
export const getMaterialsFilter = (state: RootState) => state.materials.filter;
export const getIsMaterialModalOpen = (state: RootState) => state.materials.isMaterialModalOpen;
export const getEditMaterial = (state: RootState) => state.materials.editMaterial;
export const getMaterialsLoading = (state: RootState) => state.materials.loading;

export default materialsSlice.reducer;
